// 消息分析 — 关键词 Top200 + 自定义菜单点击排行
// 微信 MP 后台「消息分析」对齐:无 7 天关键指标,只有细分数据
import { useState } from 'react'
import { Card, Segmented, Tag, Empty, Alert } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Hash, MousePointerClick, Info } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import DataTable from '@/components/DataTable'
import SourceBar from '@/components/SourceBar'
import { useMsgKeywords, useMenuClicks, type MsgKeywordItem } from '@/mocks/messages'
import { formatNumber, formatPercent } from '@/lib/utils'

type Tab = 'keywords' | 'menu'

export default function MessageAnalysis() {
  const [tab, setTab] = useState<Tab>('keywords')
  const kwQ = useMsgKeywords()
  const menuQ = useMenuClicks()

  const columns: ColumnsType<MsgKeywordItem> = [
    {
      title: '排名',
      key: 'rank',
      width: 60,
      render: (_v, _r, i) => (
        <span className="num text-ink-500 text-xs">
          {i + 1}
        </span>
      ),
    },
    {
      title: '关键词',
      dataIndex: 'keyword',
      key: 'keyword',
      width: 240,
      render: (v: string) => (
        <span className="text-ink-900 font-medium">{v}</span>
      ),
    },
    {
      title: '出现次数',
      dataIndex: 'count',
      key: 'count',
      width: 120,
      render: (v: number) => <span className="num text-ink-900 font-medium">{formatNumber(v)}</span>,
    },
    {
      title: '占比',
      dataIndex: 'ratio',
      key: 'ratio',
      width: 100,
      render: (v: number) => <span className="num text-ink-700">{formatPercent(v)}</span>,
    },
    {
      title: '类型',
      dataIndex: 'isCustom',
      key: 'isCustom',
      width: 100,
      render: (v: boolean) => (
        v
          ? <Tag color="green">自定义</Tag>
          : <Tag>通用</Tag>
      ),
    },
  ]

  const items = kwQ.data?.items ?? []
  const dateLabel = kwQ.data?.date

  return (
    <div>
      <PageHeader
        title="消息分析"
        subtitle="粉丝消息中的关键词 + 自定义菜单点击"
        extra={
          <span className="text-xs text-ink-500 num">
            {dateLabel ? `数据日期：${dateLabel}` : '加载中…'}
          </span>
        }
      />

      <div className="mb-4">
        <Segmented
          value={tab}
          onChange={(v) => setTab(v as Tab)}
          options={[
            { label: '消息关键词', value: 'keywords', icon: <Hash size={13} /> },
            { label: '菜单点击', value: 'menu', icon: <MousePointerClick size={13} /> },
          ]}
        />
      </div>

      {tab === 'keywords' && (
        <div>
          <Alert
            type="info"
            showIcon
            icon={<Info size={16} />}
            className="!mb-3"
            message="数据口径"
            description="来自公众号后台「消息分析 → 关键指标」,按出现次数降序展示 Top 200 关键词。自定义关键词由公众号管理员在后台手工配置。"
          />
          {kwQ.isLoading ? (
            <div className="text-sm text-ink-300 py-12 text-center">加载中…</div>
          ) : items.length === 0 ? (
            <Empty description="暂无关键词数据" />
          ) : (
            <Card size="small" styles={{ body: { padding: 0 } }}>
              <DataTable
                columns={columns}
                dataSource={items}
                rowKey={(r) => r.keyword}
                loading={kwQ.isFetching}
                pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t: number) => `共 ${t} 条` }}
                scroll={{ x: 600 }}
                size="small"
              />
            </Card>
          )}
        </div>
      )}

      {tab === 'menu' && (
        <div>
          <Alert
            type="info"
            showIcon
            icon={<Info size={16} />}
            className="!mb-3"
            message="数据口径"
            description="来自公众号后台「自定义菜单 → 点击量」,按总点击量降序展示。"
          />
          {menuQ.isLoading ? (
            <div className="text-sm text-ink-300 py-12 text-center">加载中…</div>
          ) : !menuQ.data || menuQ.data.items.length === 0 ? (
            <Empty description="暂无菜单点击数据" />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card title="点击量排行" size="small">
                <SourceBar
                  data={menuQ.data.items.map((m) => ({ name: m.menuName, value: m.clickCount }))}
                  maxItems={20}
                />
              </Card>
              <Card title="菜单明细" size="small">
                <DataTable
                  size="small"
                  columns={[
                    { title: '菜单名', dataIndex: 'menuName', key: 'menuName', render: (v: string) => <span className="text-ink-900">{v}</span> },
                    { title: '点击量', dataIndex: 'clickCount', key: 'clickCount', width: 100, render: (v: number) => <span className="num text-ink-900 font-medium">{formatNumber(v)}</span> },
                    { title: '点击人数', dataIndex: 'clickUser', key: 'clickUser', width: 100, render: (v: number) => <span className="num text-ink-700">{formatNumber(v)}</span> },
                    { title: '人均', dataIndex: 'avgPerUser', key: 'avgPerUser', width: 80, render: (v: number) => <span className="num text-ink-500">{v.toFixed(1)}</span> },
                  ]}
                  dataSource={menuQ.data.items}
                  rowKey={(r) => r.menuId ?? r.menuName}
                  pagination={false}
                />
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
